import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Send as SendIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Payment as PaymentIcon,
  NotificationsActive as ReminderIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import api from '../services/api';
import tenantService from '../services/tenantService';

const Messaging = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [messageType, setMessageType] = useState('sms'); // 'sms' or 'email'
  const [formData, setFormData] = useState({
    recipient_type: 'single',
    tenant_id: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    fetchTenants();
    if (tabValue === 1) {
      fetchMessageLogs();
    }
  }, [tabValue]);

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchMessageLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/messages/logs');
      setMessageLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load message logs:', error);
      setMessageLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSendSMS = async () => {
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (formData.recipient_type === 'single' && !formData.tenant_id && !formData.phone) {
      toast.error('Please select a tenant or enter a phone number');
      return;
    }

    try {
      setLoading(true);
      const data = {
        message: formData.message,
      };

      if (formData.recipient_type === 'single') {
        if (formData.tenant_id) {
          data.tenant_id = formData.tenant_id;
        } else {
          data.phone = formData.phone;
        }
      } else if (formData.recipient_type === 'all') {
        data.send_to_all = true;
      }

      await api.post('/api/messages/send-sms', data);
      toast.success('SMS sent successfully');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!formData.subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (formData.recipient_type === 'single' && !formData.tenant_id && !formData.email) {
      toast.error('Please select a tenant or enter an email address');
      return;
    }

    try {
      setLoading(true);
      const data = {
        subject: formData.subject,
        message: formData.message,
      };

      if (formData.recipient_type === 'single') {
        if (formData.tenant_id) {
          data.tenant_id = formData.tenant_id;
        } else {
          data.email = formData.email;
        }
      } else if (formData.recipient_type === 'all') {
        data.send_to_all = true;
      }

      await api.post('/api/messages/send-email', data);
      toast.success('Email sent successfully');
      resetForm();
      setOpenDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      recipient_type: 'single',
      tenant_id: '',
      phone: '',
      email: '',
      subject: '',
      message: '',
    });
  };

  const handleSendMessage = () => {
    if (messageType === 'sms') {
      handleSendSMS();
    } else {
      handleSendEmail();
    }
  };

  const handleSendBulkReminders = async () => {
    if (window.confirm('Send payment reminders to all tenants with overdue invoices?')) {
      try {
        setLoading(true);
        const response = await api.post('/api/messages/send-bulk-reminders');
        toast.success(`Sent ${response.data.sent || 0} overdue reminders successfully`);
      } catch (error) {
        toast.error('Failed to send bulk reminders');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendUnpaidReminders = async () => {
    if (window.confirm('Send SMS reminders to ALL tenants with unpaid balances (pending, partial & overdue)? Each tenant will receive a message per unpaid invoice.')) {
      try {
        setLoading(true);
        const response = await api.post('/api/messages/send-unpaid-reminders');
        toast.success(`Sent ${response.data.sent || 0} reminders (${response.data.skipped || 0} skipped)`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send reminders');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendPaybillAnnouncement = async () => {
    const confirmMsg = `This will send a paybill announcement SMS to ALL active tenants.\n\nMessage:\n"Dear [Tenant Name], please note that all payments for [Property Name] are now made via M-Pesa Paybill [Number]. Use your invoice number as the account reference."\n\nProceed?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setLoading(true);
      const response = await api.post('/api/messages/send-paybill-announcement');
      toast.success(`Paybill announcement sent to ${response.data.sent || 0} tenants (Paybill: ${response.data.paybill})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement. Make sure Paybill is set in Settings → System Config.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <PageHeader
        title="Messaging"
        subtitle="Communicate with your tenants via SMS and Email"
        breadcrumbs={[{ label: 'Messaging' }]}
        action={
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Send Message
          </Button>
        }
      />

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Send Messages" icon={<MessageIcon />} iconPosition="start" />
        <Tab label="Message History" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {/* Send Messages Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<SmsIcon />}
                    onClick={() => {
                      setMessageType('sms');
                      setOpenDialog(true);
                    }}
                  >
                    Send Custom SMS
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      setMessageType('email');
                      setOpenDialog(true);
                    }}
                  >
                    Send Custom Email
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PeopleIcon />}
                    onClick={handleSendBulkReminders}
                    disabled={loading}
                  >
                    Send Overdue Reminders
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    startIcon={<ReminderIcon />}
                    onClick={handleSendUnpaidReminders}
                    disabled={loading}
                  >
                    Remind All Unpaid Balances
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={<PaymentIcon />}
                    onClick={handleSendPaybillAnnouncement}
                    disabled={loading}
                  >
                    Announce Paybill to All Tenants
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Messaging Features
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Send individual SMS or Email to tenants
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Bulk email notifications to all tenants
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Bulk payment reminders for unpaid invoices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Message history and delivery status tracking
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Message History Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Loading message="Loading message history..." />
          ) : messageLogs.length === 0 ? (
            <EmptyState
              icon={MessageIcon}
              title="No Messages"
              description="No message history available yet"
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messageLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.sent_at)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={log.message_type === 'email' ? <EmailIcon /> : <SmsIcon />}
                          label={log.message_type?.toUpperCase() || 'SMS'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{log.tenant_name || 'N/A'}</TableCell>
                      <TableCell>{log.phone_number || log.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {log.subject && <strong>{log.subject}: </strong>}
                          {log.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Send Message Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {messageType === 'sms' ? <SmsIcon /> : <EmailIcon />}
            Send {messageType === 'sms' ? 'SMS' : 'Email'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              select
              label="Recipient Type"
              name="recipient_type"
              value={formData.recipient_type}
              onChange={handleInputChange}
            >
              <MenuItem value="single">Single Tenant</MenuItem>
              <MenuItem value="all">All Tenants</MenuItem>
            </TextField>

            {formData.recipient_type === 'single' && (
              <>
                <TextField
                  fullWidth
                  select
                  label="Select Tenant"
                  name="tenant_id"
                  value={formData.tenant_id}
                  onChange={handleInputChange}
                  helperText={messageType === 'sms' ? "Or enter phone number manually below" : "Or enter email manually below"}
                >
                  <MenuItem value="">-- Select Tenant --</MenuItem>
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.full_name} - {messageType === 'sms' ? tenant.phone : tenant.email}
                    </MenuItem>
                  ))}
                </TextField>

                {messageType === 'sms' ? (
                  <TextField
                    fullWidth
                    label="Or Enter Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+254712345678"
                    disabled={!!formData.tenant_id}
                    helperText="Format: +254..."
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Or Enter Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="tenant@example.com"
                    disabled={!!formData.tenant_id}
                  />
                )}
              </>
            )}

            {messageType === 'email' && (
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Enter email subject..."
                required
              />
            )}

            <TextField
              fullWidth
              multiline
              rows={messageType === 'email' ? 6 : 4}
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={messageType === 'email' ? 'Enter your email message...' : 'Enter your SMS message...'}
              helperText={`${formData.message.length} characters`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={loading}
            startIcon={<SendIcon />}
          >
            {loading ? 'Sending...' : `Send ${messageType === 'sms' ? 'SMS' : 'Email'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messaging;
